import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { getTranslatedCountryName } from "@/utils/country";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { useSpring } from "@react-spring/web";
import { format } from "date-fns";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";
import { useMapAutoFit } from "../hooks/use-map-auto-fit";
import type { CountryData } from "../providers/analytics-filters.provider";
import {
  returnRateColorScale,
  RETURN_RATE_THRESHOLDS,
  RETURN_RATE_COLORS,
} from "../utils/return-rate-colors";
import { numericToAlpha2 } from "./iso-country-codes";
import { useLocale } from "@babackapp/translations/use-set-locale";

// Local TopoJSON file for world countries
const GEO_URL = "/world-countries.json";

const DEFAULT_COLOR = "#e5e7eb";
const HOVER_COLOR = "#94a3b8";
const DIMMED_COLOR = "#cbd5e1"; // Neutral light gray for non-filtered countries

// Memoized Geography component for performance
const MemoizedGeography = memo(function MemoizedGeography({
  geo,
  countryData,
  isHovered,
  isFiltered,
  isDimmed,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  geo: {
    rsmKey: string;
    id?: string;
    properties: { name: string };
  };
  countryData?: CountryData;
  isHovered: boolean;
  isFiltered: boolean;
  isDimmed: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}) {
  const hasData = !!countryData;

  let fillColor: string;
  if (!hasData) {
    fillColor = DEFAULT_COLOR;
  } else if (isDimmed) {
    fillColor = DIMMED_COLOR;
  } else {
    fillColor = returnRateColorScale(countryData.returnRate);
  }

  // Add a subtle border highlight for filtered countries
  const strokeWidth = isFiltered ? 1.5 : 0.5;
  const strokeColor = isFiltered ? "#1d4ed8" : "#fff";

  return (
    <Geography
      geography={geo}
      fill={isHovered && hasData ? HOVER_COLOR : fillColor}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      style={{
        default: { outline: "none" },
        hover: { outline: "none", cursor: hasData ? "pointer" : "default" },
        pressed: { outline: "none" },
      }}
      onMouseEnter={hasData ? onMouseEnter : undefined}
      onMouseLeave={hasData ? onMouseLeave : undefined}
      onClick={hasData ? onClick : undefined}
    />
  );
});

function MapLegend() {
  const t = useTranslation();
  const maxThreshold = RETURN_RATE_THRESHOLDS[RETURN_RATE_THRESHOLDS.length - 1];

  return (
    <div className="flex items-center gap-2 mt-2">
      <span className="text-xs text-muted-foreground">0%</span>
      <div
        className="h-2 w-24 rounded"
        style={{
          background: `linear-gradient(to right, ${RETURN_RATE_COLORS.join(", ")})`,
        }}
      />
      <span className="text-xs text-muted-foreground">{maxThreshold}%+</span>
      <span className="text-xs text-muted-foreground ml-2">
        {t("analytics.map.legend")}
      </span>
    </div>
  );
}

export function CountryReturnRateMap() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { dateRange, filters, addFilter, removeFilter, availableCountries, isDataLoading } =
    useAnalyticsFilters();

  // Get active country filter values
  const activeCountryFilter = filters.get("country");
  const filteredCountryCodes = useMemo(
    () => new Set(activeCountryFilter?.value.map((v) => v.toUpperCase()) ?? []),
    [activeCountryFilter]
  );
  const hasCountryFilter = filteredCountryCodes.size > 0;

  const [locale] = useLocale();
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [tooltipContent, setTooltipContent] = useState<CountryData | null>(
    null
  );
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Container ref and dimensions for auto-fit calculation
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 228 });

  // Track container size changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Create a map for O(1) lookup by country code
  const countriesMap = useMemo(() => {
    const map = new Map<string, CountryData>();
    availableCountries.forEach((data) => {
      map.set(data.countryCode.toUpperCase(), data);
    });
    return map;
  }, [availableCountries]);

  // Set of country codes for auto-fit calculation
  const countryCodes = useMemo(
    () => new Set(availableCountries.map((c) => c.countryCode.toUpperCase())),
    [availableCountries]
  );

  // Calculate optimal projection to fit all countries with data
  const { projectionConfig: targetProjection } = useMapAutoFit({
    containerWidth: dimensions.width,
    containerHeight: dimensions.height,
    countryCodes,
    enabled: true,
  });

  // Animate projection changes smoothly
  const [animatedProjection, setAnimatedProjection] = useState(targetProjection);

  useSpring({
    scale: targetProjection.scale,
    centerX: targetProjection.center[0],
    centerY: targetProjection.center[1],
    config: {
      tension: 120,
      friction: 20,
    },
    onChange: ({ value }) => {
      setAnimatedProjection({
        scale: value.scale as number,
        center: [value.centerX as number, value.centerY as number],
      });
    },
  });

  const handleCountryClick = (countryCode: string) => {
    const data = countriesMap.get(countryCode);
    if (data) {
      const alreadySelected = filteredCountryCodes.has(countryCode);

      if (alreadySelected) {
        removeFilter({ name: "country", value: countryCode });
      } else {
        addFilter({
          name: "country",
          value: countryCode,
          condition: "in",
          isMulti: true,
        });
      }
    }
  };

  const fromDate = format(dateRange.from, "d MMM yyyy");
  const toDate = format(dateRange.to, "d MMM yyyy");

  return (
    <Card className="col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-md">{t("analytics.map.title")}</CardTitle>
        <CardDescription>
          {fromDate} - {toDate}
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div
          ref={containerRef}
          className="w-full h-[228px] relative"
          onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
        >
          <ComposableMap
            projection="geoMercator"
            projectionConfig={animatedProjection}
            width={dimensions.width}
            height={dimensions.height}
            style={{ width: "100%", height: "100%" }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  // Convert numeric ISO code to Alpha-2
                  const numericId = geo.id as string | undefined;
                  const countryCode = numericId
                    ? numericToAlpha2[numericId]
                    : undefined;
                  const countryData = countryCode
                    ? countriesMap.get(countryCode)
                    : undefined;

                  const isFiltered = countryCode
                    ? filteredCountryCodes.has(countryCode)
                    : false;
                  // Dim countries with data that are not in the active filter
                  const isDimmed =
                    hasCountryFilter && !!countryData && !isFiltered;

                  return (
                    <MemoizedGeography
                      key={geo.rsmKey}
                      geo={geo}
                      countryData={countryData}
                      isHovered={hoveredCountry === countryCode}
                      isFiltered={isFiltered}
                      isDimmed={isDimmed}
                      onMouseEnter={() => {
                        setHoveredCountry(countryCode ?? null);
                        if (countryData) {
                          setTooltipContent(countryData);
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredCountry(null);
                        setTooltipContent(null);
                      }}
                      onClick={() =>
                        countryCode && handleCountryClick(countryCode)
                      }
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          {isDataLoading && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md"
              style={{ boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.1)" }}
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm">{t("misc.loading")}</span>
              </div>
            </div>
          )}
          {tooltipContent && (
            <div
              className="pointer-events-none fixed z-50 rounded-md border bg-popover px-3 py-2 text-popover-foreground shadow-md"
              style={{
                left: mousePos.x + 12,
                top: mousePos.y - 12,
                transform: "translateY(-100%)",
              }}
            >
              <div className="flex flex-col gap-1">
                <span className="font-semibold">
                  {getTranslatedCountryName(tooltipContent.countryCode, locale)}
                </span>
                <span className="text-xs">
                  {t("analytics.map.returnRate")}:{" "}
                  <span className="font-medium">
                    {tooltipContent.returnRate.toFixed(1)}%
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {tooltipContent.returnsCount} {t("analytics.returns")} /{" "}
                  {tooltipContent.ordersCount} {t("analytics.orders")}
                </span>
              </div>
            </div>
          )}
        </div>
        <MapLegend />
      </CardContent>
    </Card>
  );
}
